<script setup lang="ts">
import { computed } from 'vue'

import type { OutcomePresentation } from '../../service/insightPresentation'

import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Icon } from '@/shared/ui/icon'

const props = defineProps<{
  outcome: OutcomePresentation
  score: number | undefined
  /** Wire hint: `currency` | `percentage` | anything else ⇒ plain number. */
  impactFormat?: string | undefined
}>()

const { t } = useI18n()
const format = useFormat()

const value = computed(() => {
  const score = Number(props.score)
  if (!Number.isFinite(score)) return ''
  if (props.impactFormat === 'currency') return format.formatCurrency(score, { maxFrac: 2 })
  if (props.impactFormat === 'percentage') {
    return format.formatPercent(score, { scale: 'percent', maxFrac: 2 })
  }
  return format.formatNumber(score, { maxFrac: 2, heavyDefault: true })
})
</script>

<template>
  <section
    class="insight-impact flex items-start gap-sm p-md border rounded-md"
    :data-outcome="outcome.key"
  >
    <span class="insight-impact__icon grid size-10 shrink-0 place-items-center rounded-md">
      <Icon :name="outcome.icon" :size="14" />
    </span>
    <div class="min-w-0 flex-1">
      <small>{{ t(outcome.labelKey) }}</small>
      <strong>{{ value }}</strong>
    </div>
  </section>
</template>

<style scoped>
.insight-impact {
  --insight-impact-accent: var(--ui-color-insight-neutral);
  --insight-impact-soft: var(--ui-color-insight-neutral-soft);
  border-color: color-mix(in srgb, var(--insight-impact-accent) 18%, var(--insight-impact-soft));
  background: color-mix(in srgb, var(--insight-impact-soft) 55%, var(--ui-color-surface));
}
.insight-impact[data-outcome='revenue_gain'] {
  --insight-impact-accent: var(--ui-color-insight-revenue);
  --insight-impact-soft: var(--ui-color-insight-revenue-soft);
}
.insight-impact[data-outcome='roas_uplift'] {
  --insight-impact-accent: var(--ui-color-insight-roas);
  --insight-impact-soft: var(--ui-color-insight-roas-soft);
}
.insight-impact[data-outcome='cost_savings'] {
  --insight-impact-accent: var(--ui-color-insight-savings);
  --insight-impact-soft: var(--ui-color-insight-savings-soft);
}
.insight-impact[data-outcome='lost_revenue'] {
  --insight-impact-accent: var(--ui-color-insight-risk);
  --insight-impact-soft: var(--ui-color-insight-risk-soft);
}
.insight-impact[data-outcome='planning'] {
  --insight-impact-accent: var(--ui-color-insight-planning);
  --insight-impact-soft: var(--ui-color-insight-planning-soft);
}

.insight-impact__icon {
  background: color-mix(in srgb, var(--insight-impact-accent) 12%, var(--insight-impact-soft));
  color: var(--insight-impact-accent);
}

.insight-impact small {
  display: block;
  color: var(--ui-color-muted);
  font-size: 0.625rem;
  line-height: 1rem;
  font-weight: 600;
  text-transform: uppercase;
}
.insight-impact strong {
  display: block;
  margin-top: 0.25rem;
  color: var(--ui-color-fg);
  font-family: var(--ui-font-numeric);
  font-size: 1.25rem;
  line-height: 1;
  font-weight: 600;
}
</style>
